import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring } from 'framer-motion';

const CustomCursor = () => {
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Position coordinates
  const mouseX = useMotionValue(-100);
  const mouseY = useMotionValue(-100);

  // Springs for smooth lag/damping effect on trailing cursor ring
  const springConfig = { stiffness: 350, damping: 28, mass: 0.5 };
  const trailX = useSpring(mouseX, springConfig);
  const trailY = useSpring(mouseY, springConfig);

  useEffect(() => {
    const handleMouseMove = (e) => {
      mouseX.set(e.clientX);
      mouseY.set(e.clientY);
      if (!isVisible) setIsVisible(true);
    };

    const handleMouseLeave = () => {
      setIsVisible(false);
    };

    const handleMouseDown = () => setClicked(true);
    const handleMouseUp = () => setClicked(false);

    // Global listener to detect hover on interactive items
    const handleMouseOver = (e) => {
      const target = e.target;
      if (!target) return;
      const isInteractive = target.closest('a, button, [role="button"], .service-card, .feature-card, .solution-card, .prod-card, input, select, textarea, .nm-btn, .theme-toggle');
      if (isInteractive) {
        setHovered(true);
      }
    };

    const handleMouseOut = (e) => {
      const target = e.target;
      if (!target) return;
      const isInteractive = target.closest('a, button, [role="button"], .service-card, .feature-card, .solution-card, .prod-card, input, select, textarea, .nm-btn, .theme-toggle');
      if (isInteractive) {
        setHovered(false);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('mouseover', handleMouseOver);
    window.addEventListener('mouseout', handleMouseOut);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('mouseover', handleMouseOver);
      window.removeEventListener('mouseout', handleMouseOut);
    };
  }, [mouseX, mouseY, isVisible]);

  if (!isVisible) return null;

  return (
    <>
      {/* 1. Inner solid pointer dot */}
      <motion.div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: '#14b8a6',
          pointerEvents: 'none',
          zIndex: 10000,
          x: mouseX,
          y: mouseY,
          translateX: '-50%',
          translateY: '-50%',
        }}
      />

      {/* 2. Trailing outer glass ring */}
      <motion.div
        animate={{
          scale: clicked ? 0.8 : hovered ? 1.8 : 1,
          borderColor: hovered ? 'rgba(20, 184, 166, 0.8)' : 'rgba(255, 255, 255, 0.25)',
          backgroundColor: hovered ? 'rgba(20, 184, 166, 0.08)' : 'rgba(255, 255, 255, 0.0)',
        }}
        transition={{ type: 'tween', duration: 0.2 }}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '32px',
          height: '32px',
          borderRadius: '50%',
          border: '1.5px solid rgba(255, 255, 255, 0.25)',
          pointerEvents: 'none',
          zIndex: 9999,
          x: trailX,
          y: trailY,
          translateX: '-50%',
          translateY: '-50%',
          boxShadow: hovered ? '0 0 16px rgba(20, 184, 166, 0.25)' : 'none',
          mixBlendMode: hovered ? 'normal' : 'difference',
        }}
      />
    </>
  );
};

export default CustomCursor;
